import { createContext, useCallback, useContext, useEffect, useMemo, useState, useRef } from 'react'
import firebase from 'firebase/compat/app'
import 'firebase/compat/auth'
import 'firebase/compat/database'
import 'firebase/compat/functions'
import * as Sentry from '@sentry/react'
import { config } from '../../../components/Init/Init'
import get from 'lodash/get'

import l10n from '../../../components/i18n/I18N'
import { useConfigValueByCurrentDevice } from '../../../hooks/useConfigValueByCurrentDevice'
import { getPlayerStatus } from '../../helpers/playerHelper'
import { disconnect, hideStore, logout, reloadPage } from '../../../sweepstakes/messages'
import { isInboxEnabled } from '../../../sweepstakes/SweepsClaimCenter/sweepsClaimCenterUtils'
import { useStateValue } from '../../state/StateProvider'
import { useRefreshFirebaseToken, useRegisterActiveTabId, useUpdateFlashPrizes } from './hooks'
import {
  useCloseNotification,
  useRemoveNotificationFromFirebase,
  useSetPendingNotifications,
  useShowNotification
} from '../../../hooks/notificationsHooks'
import { useSetPlayerFreezeInfo, useSetPlayerKyc, useSetPreviousGames } from '../../store/playerStore/playerActions'
import {
  useHandleFirebaseWallets,
  useHandleFirebaseNotificationQueue,
  useHandleFirebaseClientTags
} from './fireBaseSubscriptionsHooks'
import { redeemPromoCode, setFirebaseUserPromoCode } from '../../data/promo-code-api'
import { usePlayerStore } from '../../store'
import { useLogoutPlayer } from '../../../hooks/casinoHooks'

const FirebaseContext = createContext()
FirebaseContext.displayName = 'FirebaseContext'

export const useFirebase = () => useContext(FirebaseContext)

export const FirebaseProvider = ({ children }) => {
  const [_, dispatch] = useStateValue()

  const playerStore = usePlayerStore()
  const playerData = playerStore.useAllPlayerStore()
  const socialData = playerStore.use.socialData()
  const playerStatus = playerStore.use.status()
  const playerLoginUser = playerStore.use.loginUser()
  const playerAccountSessionId = playerStore.use.accountSessionId()
  const playerAccountSessionKicked = playerStore.use.accountSessionKicked()
  const playerKycStatus = playerStore.use.kycStatus()
  const playerTaxInfoStatus = playerStore.use.taxInfoStatus()
  const playerAccessTokenExpirationDateMs = playerStore.use.accessTokenExpirationDateMs()

  const [firebaseMaintenanceMode, setFirebaseMaintenanceMode] = useState(false)
  const enableBigWinsFeed = useConfigValueByCurrentDevice(config.enableBigWinsFeed)
  const [signingIn, setSigningIn] = useState(false)
  const [firebaseSmsInfo, setFirebaseSmsInfo] = useState(null)
  const [firebasePromoCode, setFirebasePromoCode] = useState(null)
  const [sessionIdLogoutShow, setSessionIdLogoutShow] = useState('')
  const [firebaseUserStatus, setFirebaseUserStatus] = useState(null)
  const [purchaseNotifications, setPurchaseNotifications] = useState([])
  const [callRegisterActiveTabId, setCallRegisterActiveTabId] = useState(false)
  const [firebaseCurrentUser, setFirebaseCurrentUser] = useState(null)
  const { initFlashPrizesFirebaseListening, flashPrizesQueryInterval } = useUpdateFlashPrizes(firebaseCurrentUser)

  const isFirebaseSubscriptionsSetup = useRef(false)

  const { refreshFirebaseToken } = useRefreshFirebaseToken(setFirebaseCurrentUser)

  const { removeNotificationFromFirebase } = useRemoveNotificationFromFirebase({
    purchaseNotifications,
    setPurchaseNotifications
  })
  const { closeNotification } = useCloseNotification(removeNotificationFromFirebase)
  const { showNotificationPopup } = useShowNotification(removeNotificationFromFirebase, closeNotification)
  const { pendingNotifications, setPendingNotifications } = useSetPendingNotifications(showNotificationPopup)

  const { registerActiveTabId } = useRegisterActiveTabId({ sessionIdLogoutShow, setSessionIdLogoutShow })

  const { handleFirebaseNotificationQueue } = useHandleFirebaseNotificationQueue({
    closeNotification,
    pendingNotifications,
    showNotificationPopup,
    setPendingNotifications,
    setPurchaseNotifications,
    removeNotificationFromFirebase
  })

  const { setGameBalanceReload, gameBalanceReload, handleFirebaseWallets } = useHandleFirebaseWallets()
  const { handleFirebaseClientTags } = useHandleFirebaseClientTags()

  const { setPreviousGames } = useSetPreviousGames()
  const { setPlayerFreezeInfo } = useSetPlayerFreezeInfo()
  const { setPlayerKyc } = useSetPlayerKyc()

  const { logoutPlayer } = useLogoutPlayer()

  useEffect(() => {
    if (getPlayerStatus(playerData) !== 'logged-in') {
      setFirebaseCurrentUser(null)
    }

    return () => {
      setFirebaseCurrentUser(null)
    }
  }, [playerStatus])

  useEffect(() => {
    if (callRegisterActiveTabId) {
      // need registerActiveTabId pulled out of the Firebase response, so that it will use the latest casino properties
      // (otherwise it uses the state from when the Fireback callback was defined)
      registerActiveTabId()
      setCallRegisterActiveTabId(false)
    }
  }, [callRegisterActiveTabId])

  useEffect(() => {
    if (config.redeemPromoCodesFromClient && firebaseSmsInfo && firebaseSmsInfo.smsVerified && firebasePromoCode) {
      redeemPromoCode(socialData, firebasePromoCode)
      setFirebaseUserPromoCode(null)
    }
  }, [firebasePromoCode, firebaseSmsInfo])

  if (!window.rmgBase) {
    window.rmgBase = firebase.initializeApp(config.firebaseConfig)
    console.log('[Casino] Firebase initialized...')
    const initCasinoDB = get(config.featuresByLocale[l10n.locale], 'leaderboard.enabled', false) || enableBigWinsFeed
    if (initCasinoDB) {
      window.casinoDB = firebase.initializeApp(config.leaderboardFirebaseConfig, {
        name: 'casinoDB'
      })
      console.log('[Casino] Leaderboards Firebase initialized...')
      window.casinoDBSubscriptions = []
    }
    window.messagingDBSubscriptions = []
    window.rmgBaseSubscriptions = []
    window.globalSubscriptions = []
  }

  if (window.globalSubscriptions.length === 0 && window.rmgBase) {
    window.globalSubscriptions.push(`maintenanceMode`)
    const db = window.rmgBase.database()

    db.ref(`maintenanceMode`).on('value', snapshot => {
      setFirebaseMaintenanceMode(snapshot.val() === null ? false : Boolean(snapshot.val()))
    })
  }

  let onRefreshFirebaseTokenError = null

  // note: copied from player-actions:
  // while player is partially registered, set new registration flag so we don't
  // load a pop up until the next log in
  const newRegistration = useMemo(() => {
    return playerStatus === config.status.partial && config.suppressRegisterPopup
  }, [playerStatus])

  const setupFirebaseSubscriptions = useCallback(
    setupData => {
      if (getPlayerStatus(playerData) === 'logged-in' && !newRegistration) {
        if (window.rmgBase && !signingIn && !firebaseCurrentUser) {
          const db = window.rmgBase.database()
          const messagingDb = config.firebaseUseMessagingDb
            ? window.rmgBase.database(config.messagingFirebaseConfig.databaseURL)
            : db
          const playerId = playerLoginUser.userId

          setSigningIn(true)
          refreshFirebaseToken(setupData)
            .then(() => {
              let registerSessionId = firebase
                .functions()
                .httpsCallable('registerSessionId' + config.firebaseCloudFunctionSuffix)
              console.log('[Multisession] Registering ' + playerAccountSessionId)
              return registerSessionId({
                sessionId: playerAccountSessionId,
                firebaseDb: config.firebaseDb
              })
            })
            .then(data => {
              console.log('[Multisession] Registration result', data)

              setCallRegisterActiveTabId(true)
              initFlashPrizesFirebaseListening()

              if (messagingDb) {
                const messagingSubs = config.firebaseUseMessagingDb
                  ? window.messagingDBSubscriptions
                  : window.rmgBaseSubscriptions

                messagingSubs.push(`users/${playerId}/notificationQueue`)
                // listen to messages
                handleFirebaseNotificationQueue(messagingDb, playerId)

                if (config.enableSweepstakes && config.enableClaimCenter) {
                  messagingSubs.push(`users/${playerId}/claimCenter`)
                  // listen to messages
                  messagingDb.ref(`users/${playerId}/claimCenter`).on('value', snapshot => {
                    let cc = snapshot.val()
                    dispatch({
                      type: 'setSingleModal',
                      modals: {
                        claimCenter: {
                          data: {
                            // we have to send these individually, to make sure the data structure is fully defined
                            pending: {
                              dailyBonus: cc && cc.pending && cc.pending.dailyBonus ? cc.pending.dailyBonus : null,
                              bonusHarvest: cc && cc.pending && cc.pending.bonusHarvest ? cc.pending.bonusHarvest : null
                            },
                            expired: {
                              dailyBonus: cc && cc.expired && cc.expired.dailyBonus ? cc.expired.dailyBonus : null,
                              bonusHarvest: cc && cc.expired && cc.expired.bonusHarvest ? cc.expired.bonusHarvest : null
                            }
                          }
                        }
                      }
                    })
                  })

                  if (isInboxEnabled(playerId)) {
                    if (config.inboxV2Enabled) {
                      messagingSubs.push(`users/${playerId}/inboxMessageNotifications`)
                      // listen to messages
                      messagingDb.ref(`users/${playerId}/inboxMessageNotifications`).on('value', snapshot => {
                        let notificationArr = []
                        if (snapshot.val() !== null) {
                          notificationArr = snapshot.val()
                        }

                        dispatch({
                          type: 'setSingleModal',
                          modals: {
                            claimCenter: {
                              data: {
                                notificationsSet: true,
                                notifications: notificationArr
                              }
                            }
                          }
                        })
                      })
                    }
                  }
                }
              }

              if (config.firebaseUseMessagingDb) {
                // check if we need to migrate our messaging still
                db.ref(`users/${playerId}/messagingMigrated`).once('value', snapshot => {
                  let migrated = snapshot.val()
                  if (!migrated) {
                    console.log('[Casino] Firebase notifications not yet migrated')
                    const migrateNotifications = firebase
                      .functions()
                      .httpsCallable('migrateNotifications' + config.firebaseCloudFunctionSuffix)
                    migrateNotifications({
                      playerId: playerId,
                      firebaseDb: config.firebaseDb
                    }).catch(err => {
                      console.log('[Sweeps] Error while migrating notifications: ', err)
                      if (process.env.REACT_APP_ENABLE_SENTRY === 'true') {
                        Sentry.captureException(err)
                      }
                    })
                  }
                })
              }

              window.rmgBaseSubscriptions.push(`users/${playerId}/userState/status`)
              db.ref(`users/${playerId}/userState/status`).on('value', snapshot => {
                // listen to user status change
                let snapshotVal = snapshot.val()
                console.log(`[Casino] Firebase userState/status snapshot: ${snapshotVal}`)
                setFirebaseUserStatus(snapshotVal)
              })

              // sessionKicked is deprecated. sessionEvent is used to log out players now
              window.rmgBaseSubscriptions.push(`users/${playerId}/sessionKicked`)
              db.ref(`users/${playerId}/sessionKicked`).on('value', snapshot => {
                // listen to session kicked
                if (window.sessionKickedInitialResponseCalled) {
                  let snapshotVal = snapshot.val()
                  if (snapshotVal) {
                    console.log(
                      `[Session Kicked] reason Player Limits/Responsible Gaming, session id ${playerAccountSessionKicked}, snapshot ${snapshotVal}`
                    )

                    logoutPlayer({ refresh: true })

                    if (config.enableSweepstakes) {
                      logout()
                    }
                  }
                } else {
                  window.sessionKickedInitialResponseCalled = true
                }
              })

              window.rmgBaseSubscriptions.push(`users/${playerId}/sessionData/sessionEvent`)
              db.ref(`users/${playerId}/sessionData/sessionEvent`).on('value', snapshot => {
                // ignore sessionEvent on the initial response. only act on subsequent responses
                if (window.sessionEventInitialResponseCalled) {
                  let snapshotVal = snapshot.val()
                  if (snapshotVal) {
                    switch (snapshotVal.action) {
                      case 'kickout':
                        console.log('[Session Kicked] snapshot', snapshotVal)
                        logoutPlayer({ refresh: true })

                        if (config.enableSweepstakes) {
                          logout()
                        }
                        break
                      case 'refresh':
                        console.log('[Session Refresh] sessionEvent, snapshot', snapshotVal)
                        if (config.enableSweepstakes) {
                          reloadPage()
                        }
                        break
                      default:
                        break
                    }
                  }
                } else {
                  window.sessionEventInitialResponseCalled = true
                }
              })

              if (config.enableSweepstakes && config.enableClaimCenter) {
                if (isInboxEnabled(playerId)) {
                  window.rmgBaseSubscriptions.push(`users/${playerId}/inboxMessageStatus`)
                  // listen to messages
                  db.ref(`users/${playerId}/inboxMessageStatus`).on('value', snapshot => {
                    let status = {}
                    if (snapshot.val() !== null) {
                      status = snapshot.val()
                    }

                    dispatch({
                      type: 'setSingleModal',
                      modals: {
                        claimCenter: {
                          data: {
                            inboxMessageStatusSet: true,
                            inboxMessageStatus: status
                          }
                        }
                      }
                    })
                  })
                }
              }

              window.rmgBaseSubscriptions.push(`users/${playerId}/wallets`)
              handleFirebaseWallets(db, playerId)

              window.rmgBaseSubscriptions.push(`users/${playerId}/tagsToRefresh`)
              handleFirebaseClientTags(db, playerId)

              if (config.enableSweepstakes) {
                window.rmgBaseSubscriptions.push(`users/${playerId}/kycInfo`)
                db.ref(`users/${playerId}/kycInfo`).on('value', snapshot => {
                  let snapshotVal = snapshot.val()
                  if (snapshotVal) {
                    setPlayerKyc({
                      kycStatus: snapshotVal.verificationStatus || playerKycStatus || config.kycStatuses['NotVerified'],
                      taxInfoStatus: snapshotVal.taxInfoStatus || playerTaxInfoStatus || config.taxInfoStatuses['None']
                    })
                  } else {
                    setPlayerKyc({
                      kycStatus: playerKycStatus || config.kycStatuses['NotVerified'],
                      taxInfoStatus: playerTaxInfoStatus || config.taxInfoStatuses['None']
                    })
                  }
                })

                window.rmgBaseSubscriptions.push(`users/${playerId}/freezeInfo`)
                db.ref(`users/${playerId}/freezeInfo`).on('value', snapshot => {
                  let snapshotVal = snapshot.val()
                  if (snapshotVal) {
                    setPlayerFreezeInfo(snapshotVal)
                  }
                })

                window.rmgBaseSubscriptions.push(`users/${playerId}/smsInfo`)
                db.ref(`users/${playerId}/smsInfo`).on('value', snapshot => {
                  let snapshotVal = snapshot.val()
                  // this is pulled out so that the player state is properly updated
                  setFirebaseSmsInfo(snapshotVal)
                })
                if (config.redeemPromoCodesFromClient) {
                  window.rmgBaseSubscriptions.push(`users/${playerId}/clientStorage/promoCode`)
                  db.ref(`users/${playerId}/clientStorage/promoCode`).on('value', snapshot => {
                    let promoCode = snapshot.val()
                    // this is pulled out so that the player state is properly updated
                    setFirebasePromoCode(promoCode)
                  })
                }
              }

              window.rmgBaseSubscriptions.push(`users/${playerId}/lastPlayedGameIds`)
              db.ref(`users/${playerId}/lastPlayedGameIds`).on('value', snapshot => {
                // listen to last played games
                let snapshotVal = snapshot.val()
                setPreviousGames(snapshotVal || [])
              })

              window.rmgBaseSubscriptions.push(`users/${playerId}/sessionId`)
              db.ref(`users/${playerId}/sessionId`).on('value', snapshot => {
                // listen to sessionId: if we're not allowing multi-session
                // and the id differs, log us out and show the popup
                let snapshotVal = snapshot.val()
                if (snapshotVal && snapshotVal !== playerAccountSessionId) {
                  let reason = ''
                  switch (snapshotVal) {
                    case config.sessionIdLogoutReasons.timeoutPeriod:
                    case config.sessionIdLogoutReasons.exclusionPeriod:
                    case config.sessionIdLogoutReasons.genericCatchall:
                      reason = snapshotVal
                      break
                    default:
                      if (!config.allowMultiSession) {
                        reason = config.sessionIdLogoutReasons.multiSession
                      }
                  }

                  console.log(
                    `[Multisession] Multisession reason ${reason}, session id ${playerAccountSessionId}, snapshot ${snapshotVal}`
                  )

                  if (reason !== '') {
                    logoutPlayer({ refresh: true, allowAutomaticLogin: false })

                    // inform player of logout due to multi-session/PAM
                    setSessionIdLogoutShow(reason)

                    hideStore()

                    if (config.enableSweepstakes) {
                      disconnect()
                    }
                  }
                }
              })

              isFirebaseSubscriptionsSetup.current = true
            })
            .catch(err => {
              if (err instanceof TypeError) {
                console.log('[Casino] - Firebase provider', `Name: ${err.name}; message: ${err.message}`)
                dispatch({
                  type: 'setModal',
                  modals: {
                    logout: {
                      display: true
                    }
                  }
                })
              }

              if (err && err.code) {
                if (typeof err.code === 'string' && err.code.includes('invalid-custom-token')) {
                  // logout player - custom token expired - most likely unable to run refresh (pc sleep, etc.)
                  console.log('[Casino] - Invalid custom token', err)
                  dispatch({
                    type: 'setModal',
                    modals: {
                      logout: {
                        display: true
                      }
                    }
                  })
                } else {
                  console.error(`[Casino] - Firebase ${err.code} - ${err.message}`, err)
                }
              } else {
                console.error(`[Casino] - Firebase ${err}`)
              }

              onRefreshFirebaseTokenError = err

              if (process.env.REACT_APP_ENABLE_SENTRY === 'true') {
                Sentry.captureException(err)
              }

              isFirebaseSubscriptionsSetup.current = false
            })
            .finally(() => {
              if (!onRefreshFirebaseTokenError) {
                setSigningIn(false)
              }

              dispatch({
                type: 'addTimer',
                timers: [
                  {
                    name: config.timerNames.firebase,
                    delta: config.firebaseRefreshInterval,
                    repeat: true,
                    actions: [refreshFirebaseToken]
                  }
                ]
              })
            })
        }
      }
    },
    [playerData, newRegistration, signingIn, firebaseCurrentUser, playerLoginUser]
  )

  useEffect(() => {
    if (playerAccessTokenExpirationDateMs > Date.now() && !isFirebaseSubscriptionsSetup.current) {
      setupFirebaseSubscriptions()
    }
  }, [playerAccessTokenExpirationDateMs])

  return (
    <FirebaseContext.Provider
      value={{
        signingIn,
        firebaseSmsInfo,
        firebasePromoCode,
        gameBalanceReload,
        closeNotification,
        firebaseUserStatus,
        firebaseCurrentUser,
        sessionIdLogoutShow,
        setGameBalanceReload,
        pendingNotifications,
        purchaseNotifications,
        showNotificationPopup,
        setSessionIdLogoutShow,
        setPendingNotifications,
        firebaseMaintenanceMode,
        flashPrizesQueryInterval,
        removeNotificationFromFirebase
      }}
    >
      {children}
    </FirebaseContext.Provider>
  )
}
